<template>
  <div :key="updateKey">
    <ConfirmOrDeniedDialog
      v-if="removeModal"
      v-model="removeModal"
      :mode="confirmOrDeniedDialogModes.delete"
      :message="'You can\'t restore data after delete'"
      @onDeleteClick="removeTask"
      @onCancelClick="taskIdToRemove = null; removeModal = false"
      @onCloseClick="taskIdToRemove = null; removeModal = false"
    />

    <draggable
      class="list-group"
      tag="div"
      v-bind="dragOptions"
      handle=".handle"
      :list="tasks"
      @start="startDrag"
      @end="endDrag"
      @change="changeDrag"
      :move="onMove"
      v-if="showItems"
    >
      <transition-group type="transition" :name="!drag ? 'flip-list' : null">
        <v-card
          class="list-group-item elevation-1 my-2"
          :class="cardBackground(task)"
          v-for="(task, taskKey) in tasks"
          :key="taskKey + '_' + task.Task_ID"
        >
          <v-card-title
            ref="card"
            class="pa-1 my-2 justify-space-between"
            :key="'task-update-' + nestedLevel + '-' + updateTask"
          >
            <v-col
              cols="12"
              sm="auto"
              md="8"
              class="py-0 d-flex align-center"
              @click.prevent="toggleFolder(task)"
            >
              <v-icon
                class="mr-2 handle"
                v-if="!task.Redmine_Issue_Key && !task.Jira_Task_Key"
                color="grey"
              >
                mdi-drag
              </v-icon>

              <v-icon
                class="mr-2"
                :color="task.Is_Folder ? 'primary' : 'success'"
                v-if="task.Redmine_Issue_Key || task.Jira_Task_Key"
              >
                mdi-link-variant-plus
              </v-icon>

              <v-icon
                class="mr-2"
                :color="folderIconColor(task)"
                v-text="task.Is_Folder ? (showList[task.Task_ID] ? 'mdi-folder-open' : 'mdi-folder') : 'mdi-file-code'"
              ></v-icon>

              <v-tooltip bottom color="primary">
                <template v-slot:activator="{ on, attrs }">
                  <div
                    v-on="on"
                    v-bind="attrs"
                    class="estimate-reported"
                    :class="{
                      'text--error':
                        task.Estimate > (task.Is_Folder ? task.Reported : task.Total_Reported)
                    }"
                  >
                    <h6>Est./Rep.</h6>
                    {{ (+task.Estimate).toFixed(2) }} /
                    {{
                      task.Is_Folder
                        ? (+task.Reported).toFixed(2)
                        : (+task.Total_Reported).toFixed(2)
                    }}
                  </div>
                </template>
                <span>Estimate / Reported</span>
              </v-tooltip>

              <span
                :class="[
                  task.Estimate >
                  (task.Is_Folder ? task.Reported : task.Total_Reported)
                    ? 'error--text text--darken-1'
                    : task.Is_Folder
                    ? 'primary--text text--darken-2'
                    : 'success--text text--darken-2'
                ]"
              >
                {{ task.Name }}
                <span
                  class="assigned-to"
                  v-if="
                    !task.Is_Folder &&
                    task.Assigned_To &&
                    employeeFullName(task.Assigned_To)
                  "
                >
                  /{{ employeeFullName(task.Assigned_To) }}
                </span>
              </span>
            </v-col>

            <v-col cols="12" sm="auto" md="4" class="py-0 d-flex align-center">
              <v-spacer></v-spacer>

              <v-tooltip
                v-if="task.Is_Folder && selectedTasks[task.Task_ID]?.length"
                bottom
                color="error"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    icon
                    color="error"
                    class="mr-1"
                    @click="deleteTasksFromFolder(selectedTasks[task.Task_ID])"
                  >
                    <v-icon>mdi-delete-sweep</v-icon>
                  </v-btn>
                </template>
                <span>Delete Tasks And Reports</span>
              </v-tooltip>

              <v-tooltip
                v-if="task.Is_Folder && selectedTasks[task.Task_ID]?.length"
                bottom
                color="error"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    icon
                    color="error"
                    class="mr-1"
                    @click="deleteReportsFromSelectedTasks(selectedTasks[task.Task_ID])"
                  >
                    <v-icon>mdi-file-document-remove-outline</v-icon>
                  </v-btn>
                </template>
                <span>Delete Reports Only</span>
              </v-tooltip>

              <v-checkbox
                v-if="task.Is_Folder ? parentTask && showList[task.Task_ID] : parentTask"
                dense
                hide-details
                class="d-inline-block pa-0 ma-0"
                v-model="task.isSelected"
                color="primary"
                @change="toggleSelectedTasks($event, task)"
              ></v-checkbox>

              <v-tooltip v-if="task.Is_Folder" bottom color="success">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-bind="attrs" v-on="on" icon color="success"
                    @click.prevent="showTaskDialog(true, task, true)">
                    <v-icon>mdi-folder-plus</v-icon>
                  </v-btn>
                </template>
                <span>Add folder</span>
              </v-tooltip>

              <v-tooltip v-if="task.Is_Folder" bottom color="success">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-bind="attrs" v-on="on" icon color="success"
                    @click.prevent="showTaskDialog(false, task, true)">
                    <v-icon>mdi-file-plus</v-icon>
                  </v-btn>
                </template>
                <span>Add task</span>
              </v-tooltip>

              <v-tooltip
                v-if="task.Is_Folder && !isFromIntegration(task)"
                bottom color="primary">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-bind="attrs" v-on="on" icon color="primary"
                    @click.prevent="showTaskDialog(true, task, false)">
                    <v-icon>mdi-folder-edit</v-icon>
                  </v-btn>
                </template>
                <span>Edit</span>
              </v-tooltip>

              <v-tooltip
                v-if="!task.Is_Folder"
                bottom color="primary">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-bind="attrs" v-on="on" icon color="primary"
                    @click.prevent="showTaskDialog(false, task, false)">
                    <v-icon>mdi-file-edit</v-icon>
                  </v-btn>
                </template>
                <span>Edit</span>
              </v-tooltip>

              <v-tooltip
                v-if="(task.Is_Folder && !task.Items) || (!task.Is_Folder && task.Reported === 0)"
                bottom color="error">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-bind="attrs" v-on="on" icon color="error"
                    @click.prevent="showRemoveModal(task.Task_ID)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </template>
                <span>Remove</span>
              </v-tooltip>

              <!-- Reporting -->
              <v-tooltip bottom color="primary"
                v-if="task.Is_Folder ? task.Items?.length : true">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-bind="attrs" v-on="on" icon color="primary"
                    @click.prevent="showReportingList(task)">
                    <v-icon>mdi-alpha-r-circle</v-icon>
                  </v-btn>
                </template>
                <span>Reporting</span>
              </v-tooltip>
            </v-col>
          </v-card-title>

          <DraggableTask
            v-if="task.Items && showList[task.Task_ID]"
            :key="`${task.Task_ID}-${task.isSelected ? '1' : '0'}`"
            :nestedLevel="nextLevel"
            :openFolders="openFolders"
            :parentTask="task"
            :tasks="task.Items"
            :toggleTask="task.Is_Folder ? task.isSelected : false"
          />
        </v-card>
      </transition-group>
    </draggable>
  </div>
</template>

<script>

export default {
  name: 'DraggableTask',
  props: {
    openFolders: {
      required: false,
      type: Boolean
    },
    tasks: {
      required: false,
      type: Array,
      default: null
    },
    nestedLevel: {
      required: false,
      type: Number,
      default: 0
    },
    parentTask: {
      required: false,
      type: Object
    },
    toggleTask: {
      required: false,
      type: Boolean,
      default: false
    }
  },
  components: {
    draggable: () => import('vuedraggable'),
    ConfirmOrDeniedDialog: () => import('@/components/global/ConfirmOrDeniedDialog.vue')
  },
  data () {
    return {
      drag: false,
      updateKey: 1,
      nextLevel: this.nestedLevel + 1,
      taskIdToRemove: null,
      removeModal: false,
      currentlyDraggedTask: null,
      showItems: false,
      currentTasksIDs: [],
      relatedContextListCopy: null,
      relatedContextList: null
    }
  },
  mounted () {
    if (this.tasks) {
      for (let i = 0; i < this.tasks.length; i++) {
        this.$set(this.tasks[i], 'isSelected', this.toggleTask)
        if (this.tasks[i].Is_Folder) {
          this.currentTasksIDs.push(this.tasks[i].Task_ID)
          const isExist = Object.prototype.hasOwnProperty.call(this.showList, this.tasks[i].Task_ID)
          this.$store.dispatch('updateStateOfOpenedFolders', {
            taskId: this.tasks[i].Task_ID,
            isOpen: (isExist ? this.showList[this.tasks[i].Task_ID] : !this.tasks[i].Is_Folder) || this.openFolders
          })
          this.$store.dispatch('addParentTaskOnProject', {
            parentTask: {
              Task_ID: this.tasks[i].Task_ID,
              Status: this.tasks[i].Status,
              Name: this.tasks[i].Name
            }
          })
        } else {
          this.$store.dispatch('toggleTaskToSelected', {
            task: this.tasks[i],
            action: this.toggleTask ? 'add' : 'remove'
          })
        }
        this.showItems = true
      }
    }
  },
  computed: {
    employeeFullName () {
      return employeeID => {
        return this.$store.getters.getEmployee.filter(empl => empl.employee_id === employeeID)[0]
          ? this.$store.getters.getEmployee.filter(empl => empl.employee_id === employeeID)[0].full_name
          : ''
      }
    },
    isFromIntegration () {
      return task => {
        return task.Jira_Task_ID || task.Jira_Task_Key || task.Redmine_Issue_ID || task.Redmine_Issue_Key
      }
    },
    selectedTasks () {
      return this.$store.getters.getSelectedTasks
    },
    estRepForTask () {
      return this.$store.getters.getEstRepAtTasks
    },
    showList () {
      return this.$store.getters.getOpenedFolders
    },
    updateTasks () {
      return this.$store.getters.updateTasks
    },
    user () {
      return this.$store.getters.getUser
    },
    allTasksList () {
      return this.$store.getters.getCurrentTasksList
    },
    showDeniedDialog: {
      get () {
        return this.$store.getters.getShowConfirmDeniedDialog
      },
      set (val) {
        this.$store.dispatch('showConfirmDeniedDialog', { show: val })
      }
    },
    confirmOrDeniedDialogModes () {
      return this.$store.getters.getConfirmOrDeniedDialogModes
    },
    dragOptions () {
      return {
        animation: 500,
        group: 'able-to-drop',
        disabled: false,
        ghostClass: 'ghost'
      }
    },
    isDragging () {
      return this.$store.getters.isDragging
    }
  },
  methods: {
    deleteReportsFromSelectedTasks (tasks) {
      if (tasks.length) {
        this.axios.post('/api/delete-reports-from-tasks', {
          Tasks: tasks.join(',')
        },
        {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        }).then(() => {
          this.$store.dispatch('setSuccess', { successMsg: 'Reports successfully deleted' })
          this.$store.dispatch('updateTasks', { update: true })
        }).catch(error => {
          this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
        })
      }
    },
    deleteTasksFromFolder (tasks) {
      if (tasks.length) {
        this.axios.post('/api/delete-tasks-from-folder', {
          Tasks: tasks.join(',')
        },
        {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        }).then(() => {
          this.$store.dispatch('setSuccess', { successMsg: 'Tasks successfully deleted' })
          this.$store.dispatch('updateTasks', { update: true })
        }).catch(error => {
          this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
        })
      }
    },
    toggleSelectedTasks (e, task) {
      if (!task.Is_Folder) {
        this.$store.dispatch('toggleTaskToSelected', {
          task: task,
          action: e ? 'add' : 'remove'
        })
      }
    },
    toggleFolder (task) {
      if (task.Is_Folder) {
        this.$store.dispatch('updateStateOfOpenedFolders', {
          taskId: task.Task_ID,
          isOpen: !this.showList[task.Task_ID]
        })
      }
    },
    getCoords (el) {
      const rect = el.getBoundingClientRect()
      return {
        top: rect.top + window.scrollY
      }
    },
    showReportingList (task) {
      this.$store.dispatch('setCurrentTaskData', { taskData: task })
      this.$store.dispatch('showDetailsDialog', {
        show: {
          show: true,
          repByProject: false
        }
      })
    },
    showRemoveModal (taskId) {
      this.removeModal = true
      this.taskIdToRemove = taskId
    },
    startDrag () {
      this.drag = true
      this.$store.dispatch('isDragging', { dragging: this.drag })
    },
    endDrag (e) {
      this.drag = false
      this.currentlyDraggedTask = null
      this.$store.dispatch('isDragging', { dragging: this.drag })
      this.updateKey++
    },
    onMove (e) {
      this.relatedContextListCopy = JSON.parse(JSON.stringify(e.relatedContext.list))
      this.relatedContextList = e.relatedContext.list
      if (!(e.draggedContext.element.Task_ID === this.currentlyDraggedTask)) {
        this.currentlyDraggedTask = e.draggedContext.element.Task_ID
      }
    },
    changeDrag (e) {
      if (e.added) {
        if (this.parentTask) {
          e.added.element.Parent_Task_ID = this.parentTask.Task_ID
        } else {
          e.added.element.Parent_Task_ID = null
        }
        this.updateTask(e.added.element, e.added.element.Task_ID)
        return
      }
      if (e.removed && !e.removed.element.Parent_Task_ID && this.relatedContextListCopy.length) {
        e.removed.element.Parent_Task_ID = this.relatedContextListCopy[0].Parent_Task_ID
        e.removed.element.root_task_id = this.relatedContextListCopy[0].root_task_id

        const parent = this.recursiveFindParentTask(this.tasks, this.relatedContextList)
        if (parent && parent.Status !== e.removed.element.Status) {
          this.showDeniedDialog = true
          return
        }
        this.updateTask(e.removed.element, e.removed.element.Task_ID)
      }
    },
    showTaskDialog (isFolder, task, isNew) {
      const defaultTask = {
        Assigned_To: isNew ? null : task.Assigned_To,
        Budget_SubType_ID: null,
        Description: isNew ? '' : task.Description,
        Estimate: isNew ? 0 : task.Estimate,
        Is_Folder: isFolder,
        Name: isNew ? '' : task.Name,
        Parent_Task_ID: isNew ? task.Task_ID : task.Parent_Task_ID,
        Project_ID: this.$store.getters.getSelectedProject,
        Reported: isNew ? 0 : task.Reported,
        Show_IN_Report: isNew ? true : task.Show_IN_Report,
        Start_Date: isNew ? new Date().toISOString().substr(0, 10) : new Date(task.Start_Date).toISOString().substr(0, 10),
        Status: isNew ? 'Open' : task.Status,
        Task_ID: isNew ? null : task.Task_ID,
        Jira_Task_Key: isNew ? null : task.Jira_Task_Key,
        TestTrack_ID: null,
        root_task_id: task.root_task_id,
        Rate: isNew ? null : task.Rate
      }
      this.$store.dispatch('setCurrentTaskData', { taskData: defaultTask })
      this.$store.dispatch('showTaskDialog', { show: true })
    },
    updateTask (data, taskId) {
      if (this.parentTask && this.parentTask.Status !== data.Status) {
        this.showDeniedDialog = true
        return
      }
      this.axios.put(`/api/tasks/${taskId}`, data, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        this.$store.dispatch('setSuccess', { successMsg: 'Tasks successfully updated' })
        this.$store.dispatch('updateTasks', { update: true })
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    removeTask () {
      this.axios.delete(`/api/tasks/${this.taskIdToRemove}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        this.$store.dispatch('setSuccess', { successMsg: 'Task successfully deleted' })
        this.$store.dispatch('updateTasks', { update: true })
        this.taskIdToRemove = null
        this.removeModal = false
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
        this.taskIdToRemove = null
        this.removeModal = false
      })
    },
    recursiveFindParentTask (tasks, contextList) {
      for (let i = 0; i < tasks.length; i++) {
        if (tasks[i].Is_Folder && tasks[i].Items && tasks[i].Items === contextList) {
          return tasks[i]
        }
        this.recursiveFindParentTask(tasks[i])
      }
    },
    cardBackground (task) {
      if (this.$vuetify.theme.dark) {
        return task.Reported === 0 ? 'bg-grey darken-3' : 'bg-grey darken-4'
      } else {
        return task.Reported === 0 ? 'bg-grey lighten-5' : 'bg-grey lighten-4'
      }
    },
    folderIconColor (task) {
      if (task.Status === 'Completed') return 'blue-grey lighten-2'
      return task.Is_Folder ? 'primary' : 'success'
    }
  },
  watch: {
    openFolders (newVal, oldVal) {
      for (let i = 0; i < this.tasks.length; i++) {
        this.$store.dispatch('updateStateOfOpenedFolders', {
          taskId: this.tasks[i].Task_ID,
          isOpen: newVal
        })
      }
    }
  }
}
</script>
<style lang="scss">
  .estimate-reported {
    min-width: 80px;
    margin: 0 10px 0 0;
    padding: 0;
    font-size: 0.75rem;
    line-height: 0.75rem;
    opacity: 0.6;
    white-space: nowrap;
    h6 {
      font-size: inherit;
      line-height: inherit;
    }
  }
  .assigned-to {
    font-size: 0.9rem;
  }
  .opacity-0 {
    .list-group-item {
      opacity: 0;
    }
    .ghost {
      opacity: 0.75;
        background-color: var(--v-background-base);
        border: 1px dashed var(--v-primary-base);

    }
  }
  .flip-list-move {
    transition: transform 0.5s;
  }
  .no-move {
    transition: transform 0s;
  }
  .ghost {
    opacity: 0.75;
    background: #c8ebfb;
  }
  .list-group {
    padding: 5px 5px;
    &:last-child {
      padding-top: 0;
    }
  }
  .handle {
    cursor: move;
  }
  @media screen and (max-width: 1024px) {
    .list-group {
      * {
        font-size: 0.75rem;
        line-height: 1rem;
      }
    }
    .estimate-reported {
      font-size: 0.6rem;
      line-height: 0.6rem;
    }
  }
</style>
